// Shared drape-ring compositor used by /virtual-color-draping and the
// guided self-assessment's mini draping preview. The geometry here MUST stay
// in sync with applyColorsToImage's oval clip (rx = min(w,h) * 0.35,
// ry = rx * 1.25) or the drapes reveal a different crop than the user framed.

const oval = (cx: number, cy: number, rx: number, ry: number, angle: number): [number, number] => [
  cx + rx * Math.cos(angle),
  cy + ry * Math.sin(angle),
];

export const drawDrapeOverlay = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  options?: { color?: string; segmentCount?: number; isFullCircle?: boolean }
) => {
  // Use the same math as applyColorsToImage
  const centerX = width / 2;
  const radius = Math.min(width, height) * 1.18;
  const centerY = height / 2;
  const innerRadius = radius * 0.3;
  const outerRx = radius * 0.8; // Reduced width by 20%
  const outerRy = radius * 1.25; // Keep original height
  const innerRx = innerRadius * 0.8; // Match outer width ratio
  const innerRy = innerRadius * 1.15; // Keep original height ratio
  const shoulderAngle = options?.isFullCircle ? 0 : Math.PI / 10; // ~18 degrees
  const startAngle = options?.isFullCircle ? 0 : shoulderAngle;
  const endAngle = options?.isFullCircle ? 2 * Math.PI : Math.PI - shoulderAngle;
  const segmentCount = options?.segmentCount || 12;
  const color = options?.color || "gray"; // fully opaque
  const segmentAngle = (endAngle - startAngle) / segmentCount;

  // First draw the oval guide
  const ovalRx = Math.min(width, height) * 0.35;
  const ovalRy = ovalRx * 1.25;
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(centerX, centerY, ovalRx, ovalRy, 0, 0, 2 * Math.PI);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.8)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  // Then draw the drape segments
  for (let i = 0; i < segmentCount; i++) {
    const overlap = 0.01;
    const segStart = startAngle + i * segmentAngle - overlap;
    const segEnd = startAngle + (i + 1) * segmentAngle + overlap;
    ctx.beginPath();
    const [ix, iy] = oval(centerX, centerY, innerRx, innerRy, segStart);
    ctx.moveTo(ix, iy);
    for (let a = segStart; a <= segEnd; a += 0.01) {
      const [ox, oy] = oval(centerX, centerY, outerRx, outerRy, a);
      ctx.lineTo(ox, oy);
    }
    for (let a = segEnd; a >= segStart; a -= 0.01) {
      const [ix2, iy2] = oval(centerX, centerY, innerRx, innerRy, a);
      ctx.lineTo(ix2, iy2);
    }
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.globalAlpha = 1.0;
    ctx.fill();
    ctx.globalAlpha = 1.0;
  }
};

// Vivaldi-style drape composition — the same look as /virtual-color-draping's
// applyColorsToImage (keep visually in sync with it): full-frame horizontal
// stripes of the palette, the oval-masked face on top, an optional season
// pill, and the ColorAnalysisTech watermark bottom-right.
export const drawVivaldiDrape = (
  canvas: HTMLCanvasElement,
  maskedSrc: string,
  colors: string[],
  opts?: { seasonLabel?: string }
): Promise<void> =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const ctx = canvas.getContext("2d");
      if (!ctx || colors.length === 0) {
        resolve();
        return;
      }
      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;
      const centerY = height / 2;

      // Background stripes (a single color fills the whole frame).
      const stripeHeight = height / colors.length;
      colors.forEach((color, i) => {
        ctx.fillStyle = color;
        ctx.fillRect(0, Math.floor(i * stripeHeight), width, Math.ceil(stripeHeight) + 1);
      });

      // Face oval on top of the stripes.
      const ovalRx = Math.min(width, height) * 0.35;
      const ovalRy = ovalRx * 1.25;
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(centerX, centerY, ovalRx, ovalRy, 0, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(image, 0, 0, width, height);
      ctx.restore();

      // Season pill top-center.
      if (opts?.seasonLabel) {
        const pillTextSize = width * 0.05;
        const pillPadX = pillTextSize * 0.9;
        const pillPadY = pillTextSize * 0.5;
        const label = opts.seasonLabel.replace(/\b\w/g, (c) => c.toUpperCase());
        ctx.font = `600 ${pillTextSize}px Arial, sans-serif`;
        const pillTextWidth = ctx.measureText(label).width;
        const pillWidth = pillTextWidth + pillPadX * 2;
        const pillHeight = pillTextSize + pillPadY * 2;
        const pillX = (width - pillWidth) / 2;
        const pillY = height * 0.05;
        ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
        ctx.beginPath();
        ctx.roundRect(pillX, pillY, pillWidth, pillHeight, pillHeight / 2);
        ctx.fill();
        ctx.fillStyle = "#1a1a1a";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, width / 2, pillY + pillHeight / 2);
      }

      // Watermark bottom-right.
      const logo = new Image();
      const drawWatermark = () => {
        const logoSize = width * 0.04;
        const padding = logoSize * 0.3;
        const textSize = logoSize * 0.8;
        const boxPadding = logoSize * 0.2;
        ctx.font = `${textSize}px Arial`;
        const text = "ColorAnalysisTech";
        const textWidth = ctx.measureText(text).width;
        const hasLogo = logo.complete && logo.naturalWidth > 0;
        const logoSpan = hasLogo ? logoSize + padding : 0;
        const boxWidth = logoSpan + textWidth + boxPadding * 2;
        const boxHeight = logoSize + boxPadding * 2;
        const boxX = width - boxWidth - padding;
        const boxY = height - boxHeight - padding;
        ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
        ctx.beginPath();
        ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 8);
        ctx.fill();
        if (hasLogo) {
          ctx.drawImage(logo, boxX + boxPadding, boxY + boxPadding, logoSize, logoSize);
        }
        ctx.fillStyle = "white";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(text, boxX + boxPadding + logoSpan, boxY + boxHeight / 2);
        resolve();
      };
      logo.onload = drawWatermark;
      logo.onerror = drawWatermark;
      logo.src = "/palette.svg";
    };
    image.onerror = () => resolve();
    image.src = maskedSrc;
  });

// Bake a raw photo into the standard framed oval used by the drape
// compositor: gray backdrop, centered cover fit, oval clip. Returns a data
// URL sized frameW*quality x frameH*quality.
export const bakeFramedOval = (
  src: string,
  opts?: { frameW?: number; frameH?: number; quality?: number }
): Promise<string> =>
  new Promise((resolve) => {
    const frameW = opts?.frameW ?? 300;
    const frameH = opts?.frameH ?? 375;
    const quality = opts?.quality ?? 2;
    const img = new Image();
    img.onload = () => {
      const outW = frameW * quality;
      const outH = frameH * quality;
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(src);
        return;
      }
      ctx.fillStyle = "#E0E0E0";
      ctx.fillRect(0, 0, outW, outH);

      const ovalRx = Math.min(frameW, frameH) * 0.35 * quality;
      const ovalRy = ovalRx * 1.25;
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(outW / 2, outH / 2, ovalRx, ovalRy, 0, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.clip();

      // Centered cover fit (no user pan/zoom — automatic framing).
      const cover = Math.max(outW / img.width, outH / img.height);
      const drawW = img.width * cover;
      const drawH = img.height * cover;
      ctx.drawImage(img, (outW - drawW) / 2, (outH - drawH) / 2, drawW, drawH);
      ctx.restore();

      try {
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(src);
      }
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
